import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Settings,
  Image,
  Palette,
  Globe,
  Mail,
  Phone,
  Save,
  RefreshCw,
  Eye,
  ExternalLink,
  Building2,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformSettingsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  
  const [settings, setSettings] = useState({
    platform_name: 'Fusion Builder Pro',
    tagline: 'The Complete CRM for Metal Building Sales',
    support_email: '',
    support_phone: '',
    branding: {
      logo_url: '',
      logo_link_url: '/',
      favicon_url: '',
      pwa_icon_url: '',
      primary_color: '#2563EB',
      secondary_color: '#F97316',
      page_title: 'Fusion Builder Pro',
      page_description: 'Multi-tenant SaaS CRM for metal building sales'
    },
    custom_css: '',
    google_analytics_id: '',
    facebook_pixel_id: ''
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/platform`, { headers });
      setSettings({
        ...settings,
        ...response.data,
        branding: {
          ...settings.branding,
          ...response.data.branding
        }
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/platform`, settings, { headers });
      toast.success('Platform settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateBranding = (field, value) => {
    setSettings({
      ...settings,
      branding: {
        ...settings.branding,
        [field]: value
      }
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="platform-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Settings className="w-7 h-7 text-blue-600" />
              Platform Settings
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Configure global platform branding and settings
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="branding" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Image className="w-4 h-4 mr-2" />
              Branding & Logos
            </TabsTrigger>
            <TabsTrigger value="colors" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Palette className="w-4 h-4 mr-2" />
              Colors & Theme
            </TabsTrigger>
            <TabsTrigger value="general" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Globe className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="tracking" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Eye className="w-4 h-4 mr-2" />
              Tracking
            </TabsTrigger>
          </TabsList>

          {/* Branding & Logos Tab */}
          <TabsContent value="branding" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-600" />
                  Logo Settings
                </CardTitle>
                <CardDescription>
                  Configure your platform logos and icons. These will be used as defaults for all tenants.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo URL */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Logo URL</Label>
                    <Input
                      placeholder="https://example.com/logo.png"
                      value={settings.branding.logo_url || ''}
                      onChange={(e) => updateBranding('logo_url', e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Main logo displayed in navigation</p>
                  </div>
                  <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-4 min-h-[100px]">
                    {settings.branding.logo_url ? (
                      <img 
                        src={settings.branding.logo_url} 
                        alt="Logo Preview" 
                        className="max-h-16 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Image className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">No logo set</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logo Link URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Logo Link URL</Label>
                  <Input
                    placeholder="/"
                    value={settings.branding.logo_link_url || ''}
                    onChange={(e) => updateBranding('logo_link_url', e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where the logo links to when clicked</p>
                </div>

                {/* Favicon URL */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Favicon URL</Label>
                    <Input
                      placeholder="https://example.com/favicon.ico"
                      value={settings.branding.favicon_url || ''}
                      onChange={(e) => updateBranding('favicon_url', e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Browser tab icon (16x16 or 32x32)</p>
                  </div>
                  <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-4 min-h-[80px]">
                    {settings.branding.favicon_url ? (
                      <img 
                        src={settings.branding.favicon_url} 
                        alt="Favicon Preview" 
                        className="w-8 h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Globe className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-xs">No favicon</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* PWA Icon URL */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">PWA Icon URL</Label>
                    <Input
                      placeholder="https://example.com/pwa-icon-512.png"
                      value={settings.branding.pwa_icon_url || ''}
                      onChange={(e) => updateBranding('pwa_icon_url', e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">App icon for mobile devices (512x512 recommended)</p>
                  </div>
                  <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-4 min-h-[80px]">
                    {settings.branding.pwa_icon_url ? (
                      <img 
                        src={settings.branding.pwa_icon_url} 
                        alt="PWA Icon Preview" 
                        className="w-16 h-16 object-contain rounded-xl"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Smartphone className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-xs">No PWA icon</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  SEO & Meta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Title</Label>
                  <Input
                    placeholder="Fusion Builder Pro"
                    value={settings.branding.page_title || ''}
                    onChange={(e) => updateBranding('page_title', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Description</Label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                    rows={3}
                    placeholder="Description for search engines..."
                    value={settings.branding.page_description || ''}
                    onChange={(e) => updateBranding('page_description', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors & Theme Tab */}
          <TabsContent value="colors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-blue-600" />
                  Brand Colors
                </CardTitle>
                <CardDescription>
                  These colors will be used throughout the platform and as defaults for all tenants.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Color */}
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Primary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        value={settings.branding.primary_color || '#2563EB'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.branding.primary_color || '#2563EB'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        placeholder="#2563EB"
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for buttons, links, and accents</p>
                  </div>

                  {/* Secondary Color */}
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Secondary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        value={settings.branding.secondary_color || '#F97316'}
                        onChange={(e) => updateBranding('secondary_color', e.target.value)}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.branding.secondary_color || '#F97316'}
                        onChange={(e) => updateBranding('secondary_color', e.target.value)}
                        placeholder="#F97316"
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for highlights and secondary elements</p>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="p-6 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Preview</h4>
                  <div className="flex flex-wrap gap-4">
                    <button 
                      className="px-4 py-2 rounded-lg text-white font-medium"
                      style={{ backgroundColor: settings.branding.primary_color }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white font-medium"
                      style={{ backgroundColor: settings.branding.secondary_color }}
                    >
                      Secondary Button
                    </button>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: settings.branding.primary_color }}
                      >
                        M
                      </div>
                      <span className="text-sm" style={{ color: settings.branding.secondary_color }}>
                        Accent Text
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom CSS */}
            <Card>
              <CardHeader>
                <CardTitle>Custom CSS</CardTitle>
                <CardDescription>
                  Add custom CSS to override default styles (advanced)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600 font-mono text-sm"
                  rows={8}
                  placeholder="/* Custom CSS */&#10;.my-class {&#10;  color: #333;&#10;}"
                  value={settings.custom_css || ''}
                  onChange={(e) => setSettings({ ...settings, custom_css: e.target.value })}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Platform Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Platform Name</Label>
                    <Input
                      value={settings.platform_name || ''}
                      onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Tagline</Label>
                    <Input
                      value={settings.tagline || ''}
                      onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Support Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Support Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="support@fusionbuilderpro.com"
                        value={settings.support_email || ''}
                        onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-200">Support Phone</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="tel"
                        placeholder="(555) 000-0000"
                        value={settings.support_phone || ''}
                        onChange={(e) => setSettings({ ...settings, support_phone: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tracking Tab */}
          <TabsContent value="tracking" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Analytics & Tracking
                </CardTitle>
                <CardDescription>
                  Add tracking codes for analytics platforms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Google Analytics ID</Label>
                  <Input
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXX-X"
                    value={settings.google_analytics_id || ''}
                    onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Facebook Pixel ID</Label>
                  <Input
                    placeholder="XXXXXXXXXXXXXXXX"
                    value={settings.facebook_pixel_id || ''}
                    onChange={(e) => setSettings({ ...settings, facebook_pixel_id: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PlatformSettingsPage;
